if (!customElements.get('bundle-selector')) {
  customElements.define(
    'bundle-selector',
    class BundleSelector extends HTMLElement {
      connectedCallback() {
        this.rules = this.parseRules();
        this.statusEl = this.querySelector('[data-bundle-status]');
        this.formId = this.dataset.productFormId;
        this.parentVariantId = this.dataset.parentVariantId;
        this.customerTags = this.normalizeTags(this.dataset.customerTags || '');
        this.addEventListener('change', () => this.sync());
        this.ensureValidSelections();
        this.sync();
        window.MusicPlanetBundle = this;
      }

      parseRules() {
        const el = this.querySelector('[data-bundle-rules]');
        if (!el) return { mainVariantIds: [], sets: [], eligibleTags: [] };
        try {
          return JSON.parse(el.textContent);
        } catch (e) {
          return { mainVariantIds: [], sets: [], eligibleTags: [] };
        }
      }

      normalizeTags(value) {
        const list = Array.isArray(value)
          ? value
          : String(value || '')
              .split(',')
              .flatMap((part) => part.split(','));
        return list.map((tag) => String(tag).trim().toLowerCase()).filter(Boolean);
      }

      isAvailableInput(input) {
        return input && !input.disabled && input.dataset.available !== 'false';
      }

      requiredEligibleTags() {
        return this.normalizeTags(
          Array.isArray(this.rules.eligibleTags) ? this.rules.eligibleTags : []
        );
      }

      customerIsLoggedIn() {
        return this.dataset.customerLoggedIn === 'true';
      }

      customerIsEligible() {
        if (this.dataset.eligible === 'true') return true;
        if (this.dataset.eligible === 'false') return false;
        const required = this.requiredEligibleTags();
        if (!required.length) return true;
        if (!this.customerTags.length) return false;
        return required.some((tag) => this.customerTags.includes(tag));
      }

      requiresEligibleTags() {
        return this.requiredEligibleTags().length > 0;
      }

      ensureValidSelections() {
        const mainChecked = this.querySelector('input[name="bundle_main_variant"]:checked');
        if (mainChecked && !this.isAvailableInput(mainChecked)) {
          mainChecked.checked = false;
          const firstAvailable = this.querySelector(
            'input[name="bundle_main_variant"]:not(:disabled)[data-available="true"]'
          );
          if (firstAvailable) firstAvailable.checked = true;
        }

        this.querySelectorAll('[data-bundle-set]').forEach((fieldset) => {
          fieldset.querySelectorAll('input:checked').forEach((input) => {
            if (!this.isAvailableInput(input)) input.checked = false;
          });
          const required = fieldset.dataset.required !== 'false';
          const max = parseInt(fieldset.dataset.max || '1', 10);
          const hasChecked = fieldset.querySelectorAll('input:checked').length > 0;
          if (required && max <= 1 && !hasChecked) {
            const firstAvailable = fieldset.querySelector(
              'input:not(:disabled)[data-available="true"]'
            );
            if (firstAvailable) firstAvailable.checked = true;
          }
        });
      }

      getSelectedMainInput() {
        const input = this.querySelector('input[name="bundle_main_variant"]:checked');
        return this.isAvailableInput(input) ? input : null;
      }

      getSelectedMainId() {
        return this.getSelectedMainInput()?.value || null;
      }

      getSelectedComponents() {
        const components = [];
        this.querySelectorAll('[data-bundle-set]').forEach((fieldset) => {
          const setKey = fieldset.dataset.bundleSet;
          fieldset.querySelectorAll('input:checked').forEach((input) => {
            if (!this.isAvailableInput(input)) return;
            components.push({
              id: input.value,
              set: setKey,
              title: input.dataset.title || input.value,
            });
          });
        });
        return components;
      }

      stockMessage() {
        const availableMain = parseInt(this.dataset.availableMainCount || '0', 10);
        if (availableMain < 1) {
          return 'This bundle is unavailable because all main products are sold out.';
        }

        for (const fieldset of this.querySelectorAll('[data-bundle-set]')) {
          const required = fieldset.dataset.required !== 'false';
          const availableCount = parseInt(fieldset.dataset.availableCount || '0', 10);
          if (required && availableCount < 1) {
            const name =
              fieldset.querySelector('legend')?.childNodes?.[0]?.textContent?.trim() ||
              'a required group';
            return `This bundle is unavailable because ${name} is sold out.`;
          }
        }

        if (!this.getSelectedMainId()) {
          return 'Select an in-stock main product to add to cart.';
        }

        for (const fieldset of this.querySelectorAll('[data-bundle-set]')) {
          const required = fieldset.dataset.required !== 'false';
          const max = parseInt(fieldset.dataset.max || '1', 10);
          const selected = Array.from(fieldset.querySelectorAll('input:checked')).filter((input) =>
            this.isAvailableInput(input)
          ).length;
          if (required && selected < 1) {
            return 'Select required in-stock bundle options to add to cart.';
          }
          if (selected > max) {
            return 'Too many items selected in a bundle group.';
          }
        }

        return null;
      }

      isComplete() {
        return this.stockMessage() === null;
      }

      sync() {
        this.querySelectorAll('[data-bundle-set]').forEach((fieldset) => {
          const max = parseInt(fieldset.dataset.max || '1', 10);
          if (max <= 1) return;
          const checked = Array.from(fieldset.querySelectorAll('input:checked')).filter((input) =>
            this.isAvailableInput(input)
          );
          if (checked.length >= max) {
            fieldset.querySelectorAll('input:not(:checked)').forEach((input) => {
              if (input.closest('.bundle-selector__option--unavailable')) return;
              if (input.dataset.available === 'false') return;
              if (!input.hasAttribute('data-was-disabled')) {
                input.setAttribute('data-was-disabled', '1');
                input.disabled = true;
              }
            });
          } else {
            fieldset.querySelectorAll('input[data-was-disabled]').forEach((input) => {
              if (input.closest('.bundle-selector__option--unavailable')) return;
              if (input.dataset.available === 'false') return;
              input.disabled = false;
              input.removeAttribute('data-was-disabled');
            });
          }
        });

        const message = this.stockMessage();
        const complete = message === null;
        const form = document.getElementById(this.formId);
        const submit = form?.querySelector('[type="submit"][name="add"]');
        if (submit) {
          if (complete) {
            submit.removeAttribute('disabled');
            submit.removeAttribute('aria-disabled');
          } else {
            submit.setAttribute('disabled', 'disabled');
            submit.setAttribute('aria-disabled', 'true');
          }
        }

        this.updatePricingLabels();

        if (this.statusEl) {
          if (message) {
            this.statusEl.hidden = false;
            this.statusEl.dataset.error = 'true';
            this.statusEl.textContent = message;
          } else if (this.requiresEligibleTags() && !this.customerIsEligible()) {
            this.statusEl.hidden = false;
            this.statusEl.dataset.error = 'false';
            const tags = this.requiredEligibleTags().join(', ');
            if (!this.customerIsLoggedIn()) {
              const loginUrl = this.dataset.loginUrl || '/account/login';
              this.statusEl.innerHTML = `Log in with an eligible account (${tags}) to get bundle pricing. <a href="${loginUrl}">Log in</a> — otherwise items are added at full price.`;
            } else {
              this.statusEl.textContent = `Your account is not eligible for bundle pricing (${tags}). Selected items will be added at full price.`;
            }
          } else {
            this.statusEl.hidden = true;
            this.statusEl.dataset.error = 'false';
            this.statusEl.textContent = '';
          }
        }
      }

      updatePricingLabels() {
        const eligible = this.customerIsEligible();
        this.querySelectorAll('[data-bundle-set] .bundle-selector__option-price').forEach((el) => {
          const input = el.closest('label')?.querySelector('input');
          if (!input || input.dataset.available === 'false') return;
          if (eligible) {
            el.textContent = 'Included';
            el.classList.add('bundle-selector__option-price--included');
          } else {
            const price = input.dataset.price;
            el.textContent = price || 'Full price';
            el.classList.remove('bundle-selector__option-price--included');
          }
        });
      }

      buildCartItems() {
        if (!this.isComplete()) return null;
        const mainInput = this.getSelectedMainInput();
        const components = this.getSelectedComponents();
        if (!mainInput) return null;

        if (!this.customerIsEligible()) {
          return [
            {
              id: Number(mainInput.value),
              quantity: 1,
            },
            ...components.map((component) => ({
              id: Number(component.id),
              quantity: 1,
            })),
          ];
        }

        const bundleId =
          typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `bundle-${Date.now()}-${Math.random().toString(16).slice(2)}`;

        const lines = [
          {
            i: String(mainInput.value),
            r: 'main',
            t: mainInput.dataset.title || mainInput.value,
          },
          ...components.map((component) => ({
            i: String(component.id),
            r: 'component',
            s: component.set,
            t: component.title,
          })),
        ];

        const includes = lines.map((line) => line.t).join(', ');

        return [
          {
            id: Number(this.parentVariantId),
            quantity: 1,
            properties: {
              _bundle_id: bundleId,
              _bundle_role: 'parent',
              _bundle_expand: JSON.stringify({ lines }),
              Includes: includes,
            },
          },
        ];
      }
    }
  );
}
